import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { backendToRawSemanticFields } from '@framework/common-web/engine/engine-validation/parseSemanticFields.ts';
import { useFillableRegisters } from '@framework/common-web/engine/hooks/useFillableRegisters.tsx';
import { getBlockingError } from '@framework/common-web/schema/procedure/getBlockingError.ts';
import { useProcedureDossierSemanticFields } from '@framework/common-web/schema/procedure/hooks/useProcedureDossierSemanticFields.ts';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import { type BrandedPath } from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../loading/LoadingPage.tsx';
import { DossierCard } from '../components/DossierCard.tsx';
import { useDossier } from './hooks/useDossier.ts';
import { DossierDataTab } from './tab/DossierDataTab.tsx';
import { DossierDescriptionsTab } from './tab/DossierDescriptionsTab.tsx';
import { DossierPermissionsTab } from './tab/DossierPermissionsTab.tsx';
import { DossierSubmissionTab } from './tab/DossierSubmissionTab.tsx';
import { DossierPermissionLogic } from './tab/dossier-permission/DossierPermissionLogic.ts';

interface Params {
  id: string;
}

export type IDossierPageProps = RouteComponentProps<Params>;

export function DossierPage(props: IDossierPageProps) {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<string>('data');

  const [id] = useIdFromUrl(props, 'id');

  const dossierData = useDossier(id);
  const {
    registers,
    error: registersError,
    isLoading: isLoadingRegisters,
  } = useFillableRegisters();

  const user = useCurrentUser();
  const canViewPermissions = DossierPermissionLogic.canViewPermissions(
    user?.utenteLoggato,
    dossierData.procedure?.pratica?.personeFisiche,
  );
  const canEditPermissions = DossierPermissionLogic.canEditPermissions(
    user?.utenteLoggato,
    dossierData.procedure?.pratica?.personeFisiche,
  );

  const {
    data: dossierSemanticFieldsData,
    isLoading: isLoadingSemanticFields,
    error: semanticFieldsError,
  } = useProcedureDossierSemanticFields(dossierData?.procedure);

  const blockingSemanticFieldsError = getBlockingError(semanticFieldsError);

  const dossierSemanticFields = useMemo(
    () =>
      dossierSemanticFieldsData
        ? backendToRawSemanticFields(dossierSemanticFieldsData)
        : undefined,
    [dossierSemanticFieldsData],
  );

  useBreadcrumbItem({
    path: '/pratiche',
    title: t('pratiche.pratiche', 'Pratiche'),
  });

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('pratiche.dettaglio', 'Dettaglio pratica'), // TODO Sostituire con titolo pratica
  });

  if (!id) {
    return (
      <NetworkAlertPage
        message={t(
          'pratiche.errore.no-id',
          'Nessun ID trovato nella richiesta.',
        )}
      />
    );
  }

  if (dossierData.isLoading || isLoadingRegisters || isLoadingSemanticFields) {
    return <LoadingPage />;
  }

  if (
    dossierData.error ??
    registersError ??
    blockingSemanticFieldsError ??
    !dossierData.procedure
  ) {
    return (
      <NetworkAlertPage
        message={t(
          'pratiche.errore.network',
          'Errore durante il caricamento della pratica',
        )}
        error={
          dossierData.error ?? registersError ?? blockingSemanticFieldsError
        }
      />
    );
  }

  return (
    <PageLayout>
      <DossierCard isDossierPage procedureDossier={dossierData.procedure}>
        <ResponsiveCard
          activeTabKey={activeTab}
          tabList={[
            {
              key: 'data',
              label: t('pratiche.dati', 'Dati'),
            },
            {
              key: 'descriptions',
              label: t('pratiche.info', 'Informazioni'),
            },
            {
              key: 'submissions',
              label: t('pratiche.submissions', 'Richieste'),
            },
            ...(canViewPermissions
              ? [
                  {
                    key: 'permissions',
                    label: t('pratiche.permissions', 'Permessi e Visibilità'),
                  },
                ]
              : []),
          ]}
          onTabChange={key => setActiveTab(key)}
        >
          {activeTab === 'data' && (
            <DossierDataTab
              id={id}
              sections={dossierData.sections}
              dossierSemanticFields={dossierSemanticFields}
              registers={registers}
            />
          )}
          {activeTab === 'descriptions' && (
            <DossierDescriptionsTab dossierProcedure={dossierData.procedure} />
          )}
          {activeTab === 'submissions' && (
            <DossierSubmissionTab
              id={id}
              dossierProcedure={dossierData.procedure}
            />
          )}
          {activeTab === 'permissions' && (
            <DossierPermissionsTab id={id} readonly={!canEditPermissions} />
          )}
        </ResponsiveCard>
      </DossierCard>
    </PageLayout>
  );
}
